import React from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { Button, Skeleton } from '@/nova/components';

import {
  isImmersive,
  landingSampleParams,
  landingStudioColours,
  orderedTemplates,
  previewLandingSample,
  withInertLinks,
  type LandingGame,
  type LandingParams,
  type LandingStudioColours,
  type LandingTemplate,
} from '../../landing-page-api';
import type { PlayableTemplate } from '../../playable-api';
import { readStudioColours } from '../../studio-preview-colours';
import { TemplatePicker, type PreviewState } from '../PlayableComposer/TemplatePicker';
import { DEVICE_VIEWPORT } from './LandingPreview';
import * as S from './LandingPageComposer.styles';

/** The template catalogue as read: loading, the list, or failed. */
export type TemplatesRead = { status: 'loading' } | { status: 'ready'; list: LandingTemplate[] } | { status: 'error' };

/** A card's preview is a phone screen (the page's top on a phone, a full-screen game's whole screen). Layout. */
const CARD_FORMAT = `${DEVICE_VIEWPORT.mobile.w}x${DEVICE_VIEWPORT.mobile.h}`;
/** The card preview box the phone screen is shrunk into. Layout. */
// A phone screen at a size that reads at a glance; the three cards share the whole row (see TemplatePicker `fit`).
const CARD_W = 280;
const CARD_H = 560;

/** A full-screen game's kind, in the owner's words. */
export const gameKindLabel = (game: LandingGame | undefined, t: TFunction): string =>
  game === 'wheel' ? t('Wheel') : game === 'pick' ? t('Pick a prize') : t('Game');

/** A template's kind, for its card and the composer's template line: "Game · Wheel", or "Page". */
export const templateKindLabel = (tpl: LandingTemplate, t: TFunction): string =>
  isImmersive(tpl) ? t('Game · {{kind}}', { kind: gameKindLabel(tpl.game, t) }) : t('Page');

/** A landing template as a card of the shared template picker (the mini-game cards): its name, and its kind with its
 *  description as the card's text. Pure. */
const asPickerCard = (tpl: LandingTemplate, t: TFunction): PlayableTemplate => ({
  id: tpl.id,
  label: tpl.name || tpl.id,
  category: 'landing_page',
  subjects: [],
  formats: [CARD_FORMAT],
  schema_version: 0,
  schema: { description: [templateKindLabel(tpl, t), tpl.description.trim()].filter(Boolean).join(' · ') },
  assets: [],
});

/** What the cards are drawn in: not read yet, the Studio's colours, or null (unreadable: the brand's own). */
type ColoursRead = { read: false } | { read: true; colours: LandingStudioColours | null };

export interface LandingTemplatePickerProps {
  tenantId: number;
  templates: TemplatesRead;
  /** The page as it is (a card spark-creatives cannot draw as a sample previews the page on that template). */
  params: LandingParams;
  /** The page's template as resolved ('' none yet). */
  selectedId: string;
  /** The page has a template: Cancel goes back to the composer. */
  canCancel: boolean;
  disabled?: boolean;
  onPick: (templateId: string) => void;
  onCancel: () => void;
}

/**
 * The landing page's template step, before the composer (like a new mini-game's game picker): one card per template,
 * the regular offer page first, each with a live mini-preview of the template on a phone screen. The previews are
 * spark-creatives' sample mode: sample copy, a neutral welcome offer and the brand's own logo, drawn in the Studio's
 * colours (re-read when its theme or accent changes) with the template's built-in drawings instead of any scene art,
 * so a card promises the layout, not a look the page's own art will repaint. Picking one opens the composer on it.
 */
export const LandingTemplatePicker = ({
  tenantId,
  templates,
  params,
  selectedId,
  canCancel,
  disabled = false,
  onPick,
  onCancel,
}: LandingTemplatePickerProps): React.ReactElement => {
  const { t } = useTranslation();
  const list = React.useMemo(() => (templates.status === 'ready' ? orderedTemplates(templates.list) : []), [templates]);
  const cards = React.useMemo(() => list.map((tpl) => asPickerCard(tpl, t)), [list, t]);

  // The Studio's colours, resolved for the sandboxed frames: read on mount and again whenever the theme provider flips
  // `data-ows-theme` / `data-ows-accent` on <html> (it does so in an effect after a render, never during it).
  const [colours, setColours] = React.useState<ColoursRead>({ read: false });
  React.useEffect(() => {
    if (typeof document === 'undefined') {
      setColours({ read: true, colours: null });
      return undefined;
    }
    const read = (): void => {
      const studio = readStudioColours();
      const next = studio ? landingStudioColours(studio) : null;
      // The same colours again (an attribute flip that changes nothing) keep the held value: nothing is asked again.
      setColours((cur) =>
        cur.read && JSON.stringify(cur.colours) === JSON.stringify(next) ? cur : { read: true, colours: next },
      );
    };
    read();
    const observer = new MutationObserver(read);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-ows-theme', 'data-ows-accent'],
    });
    return () => observer.disconnect();
  }, []);

  // The page each fallback card is drawn from: as it was when the step opened (nothing is edited meanwhile).
  const paramsRef = React.useRef(params);
  const [previews, setPreviews] = React.useState<Record<string, PreviewState>>({});
  React.useEffect(() => {
    // Syncs with spark-creatives: one preview per card, again when the Studio's colours change (a card on screen keeps
    // its document until the new one lands, then cross-fades to it).
    if (!colours.read || list.length === 0) return undefined;
    const controller = new AbortController();
    const studio = colours.colours;
    setPreviews((cur) =>
      Object.fromEntries(
        list.map((tpl) => [tpl.id, cur[tpl.id]?.status === 'ready' ? cur[tpl.id] : { status: 'loading' }]),
      ),
    );
    list.forEach((tpl) => {
      void previewLandingSample(tenantId, landingSampleParams(paramsRef.current, tpl), studio, controller.signal).then(
        (res) => {
          if (controller.signal.aborted) return;
          if (!res.ok && res.aborted) return;
          const next: PreviewState = res.ok
            ? { status: 'ready', html: withInertLinks(res.data.html) }
            : { status: res.status === 404 ? 'unavailable' : 'error' };
          setPreviews((cur) => ({ ...cur, [tpl.id]: next }));
        },
      );
    });
    return () => controller.abort();
  }, [tenantId, list, colours]);

  return (
    <S.PickerWrap>
      <S.PickerHead>
        <S.PickerIntro>
          <S.SectionTitle text="Choose a template" />
          <S.Hint text="A full-screen game or a regular offer page. Every template leads to your welcome offer." />
        </S.PickerIntro>
        {canCancel ? (
          <S.PickerActions>
            <Button variant="text" label={t('Cancel')} onClick={onCancel} />
          </S.PickerActions>
        ) : null}
      </S.PickerHead>
      {templates.status === 'loading' ? (
        <Skeleton block height={320} radius={12} />
      ) : templates.status === 'error' ? (
        <S.Hint text="The templates couldn't be loaded. Close this page and open it again to retry." />
      ) : cards.length === 0 ? (
        <S.Hint text="No template is available yet." />
      ) : (
        <TemplatePicker
          templates={cards}
          selectedId={selectedId}
          previews={previews}
          canPreview
          previewFormat={CARD_FORMAT}
          disabled={disabled}
          cardW={CARD_W}
          cardH={CARD_H}
          fit
          ariaLabel={t('Template')}
          onPick={onPick}
        />
      )}
    </S.PickerWrap>
  );
};

export default LandingTemplatePicker;
